/**
 * Unified and robust utility to resolve backend media URLs across all environments.
 * Handles:
 * 1. Missing or undefined environment variables.
 * 2. Mixed content issues by matching page protocols.
 * 3. Windows localhost vs 127.0.0.1 DNS resolution mismatches.
 * 4. Normalization of clean paths (including appending /media/ for relative product paths).
 */
export const resolveMediaUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;

  // 1. Get base API URL from environment variable, falling back to window location or localhost
  let apiUrl = process.env.REACT_APP_API_URL || "";

  if (!apiUrl) {
    // In local development, if we're on localhost, backend is usually on port 8000
    if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
      apiUrl = `http://${window.location.hostname}:8000`;
    } else {
      // Fallback to production backend URL
      apiUrl = "https://gio-billing-backend-production.up.railway.app";
    }
  }

  // 2. Ensure protocol is present and correct
  if (apiUrl && !apiUrl.startsWith("http://") && !apiUrl.startsWith("https://")) {
    const protocol = window.location.protocol === "https:" ? "https://" : "http://";
    apiUrl = `${protocol}${apiUrl}`;
  }

  // 3. Dynamically handle localhost vs 127.0.0.1 on Windows to bypass DNS loopback issues
  if (window.location.hostname === "127.0.0.1") {
    apiUrl = apiUrl.replace("localhost", "127.0.0.1");
  } else if (window.location.hostname === "localhost") {
    apiUrl = apiUrl.replace("127.0.0.1", "localhost");
  }

  // 4. Normalize clean base and clean path
  const base = apiUrl.replace(/\/$/, "");
  let cleanPath = url;
  
  if (!cleanPath.startsWith("/") && !cleanPath.startsWith("media/")) {
    cleanPath = `/media/${cleanPath}`;
  } else if (cleanPath.startsWith("/") && !cleanPath.startsWith("/media/")) {
    cleanPath = `/media${cleanPath}`;
  } else if (!cleanPath.startsWith("/")) {
    cleanPath = `/${cleanPath}`;
  }

  return `${base}${cleanPath}`;
};

export default resolveMediaUrl;
